import BookingCard from '@/components/BookingCard';
import LocationSection from '@/components/LocationSection';
import MobileBookingBar from '@/components/MobileBookingBar';
import PropertyAmenities from '@/components/PropertyAmenities';
import PropertyCalendar from '@/components/PropertyCalendar';
import PropertyDescription from '@/components/PropertyDescription';
import PropertyGallery from '@/components/PropertyGallery';
import PropertyHeader from '@/components/PropertyHeader';
import PropertyHighlights from '@/components/PropertyHighlights';
import PropertyHost from '@/components/PropertyHost';
import PropertyInfo from '@/components/PropertyInfo';
import ReviewsSection from '@/components/ReviewsSection';
import SEOHead from '@/components/SEOHead';
import Layout from '@/components/v2/organisms/landing/layout/Layout';

import { usePropertyBySlug } from '@/hooks/data/usePropertyBySlug';
import { usePropertySEOData } from '@/hooks/usePropertySEOData';
import { useSupabaseAvailability } from '@/hooks/useSupabaseAvailability';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';
import { getPropertyShortName } from '@/utils/propertyUtils';

import { useSearchParamsState } from '@/hooks/search/useSearchParamsState';
import { useSearchUrlSync } from '@/hooks/search/useSearchUrlSync';
import { memo, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const PropertyDetail = memo(() => {
  const { title: titleParam } = useParams();

  const { checkInDate, checkOutDate, guests } = useSearchParamsState(150);
  const { replaceParams, clearParams } = useSearchUrlSync();

  const [shouldResetGuests, setShouldResetGuests] = useState(false);

  const { trackPropertyDetailViewed } = useAmplitudeEvents();
  const eventTrackedRef = useRef<string>('');

  const seoData = usePropertySEOData(titleParam || '');
  const {
    data: property,
    isLoading,
    error,
  } = usePropertyBySlug(titleParam || '');
  const { unavailableDates, isLoading: availabilityLoading } =
    useSupabaseAvailability(property?.id || '');

  useEffect(() => {
    if (property && !isLoading) {
      const propertyKey = `${property.id}-${property.title}`;
      if (eventTrackedRef.current !== propertyKey) {
        const propertyShortName = getPropertyShortName(property.title);
        const totalReviews = property.reviews ? property.reviews.length : 0;
        const totalPhotos = property.images ? property.images.length : 0;
        const hostName = property.host ? property.host.name : '';

        trackPropertyDetailViewed(
          property.title,
          propertyShortName,
          property.rating,
          property.guests,
          property.price,
          totalReviews,
          totalPhotos,
          hostName,
        );

        eventTrackedRef.current = propertyKey;
      }
    }
  }, [property, isLoading, trackPropertyDetailViewed]);

  const handleDateChange = (ci?: Date, co?: Date) => {
    replaceParams({ checkIn: ci, checkOut: co, guests: String(guests ?? 1) });
  };

  const handleClearGuests = () => {
    clearParams(['checkin', 'checkout', 'guests']);
    setShouldResetGuests(true);
  };

  if (isLoading) {
    return (
      <Layout>
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          canonicalUrl={seoData.canonicalUrl}
          image={seoData.image}
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando propiedad...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !property) {
    return (
      <Layout>
        <SEOHead
          title="Propiedad no encontrada | Estancia Corta"
          description="La propiedad que buscas no está disponible. Explora otros alojamientos en Manzanillo."
          canonicalUrl={seoData.canonicalUrl}
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Propiedad no encontrada
            </h1>
            <p className="text-gray-600">La propiedad que buscas no existe.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        canonicalUrl={seoData.canonicalUrl}
        image={seoData.image}
      />

      <PropertyHeader
        title={property.title}
        rating={property.rating}
        neighborhood={property.neighborhood}
        guests={property.guests}
        bedrooms={property.bedrooms}
        bathrooms={property.bathrooms}
      />

      <PropertyGallery images={property.images} title={property.title} />

      <section className="pb-2 md:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div>
                <PropertyInfo
                  guests={property.guests}
                  bedrooms={property.bedrooms}
                  bathrooms={property.bathrooms}
                />

                <div className="py-6 border-b border-gray-200">
                  <PropertyHost host={property.host} />
                  <PropertyHighlights
                    highlights={property.propertyHighlights}
                  />
                </div>

                <PropertyDescription description={property.longDescription} />
                <PropertyAmenities propertyId={property.id} />
                <LocationSection property={property} />

                <PropertyCalendar
                  externalCheckIn={checkInDate ?? undefined}
                  externalCheckOut={checkOutDate ?? undefined}
                  unavailableDates={unavailableDates}
                  onDateChange={handleDateChange}
                  onClear={handleClearGuests}
                />

                {property.reviews && property.reviews.length > 0 && (
                  <ReviewsSection reviews={property.reviews} />
                )}
              </div>
            </div>

            <div className="lg:col-span-1">
              <BookingCard
                price={property.price}
                externalCheckIn={checkInDate ?? undefined}
                externalCheckOut={checkOutDate ?? undefined}
                onDateChange={handleDateChange}
                maxGuests={property.guests}
                propertyTitle={property.title}
                unavailableDates={unavailableDates}
                availabilityLoading={availabilityLoading}
                airbnbUrl={property.airbnbUrl}
                shouldResetGuests={shouldResetGuests}
                onGuestsReset={() => setShouldResetGuests(false)}
                property={{
                  id: property.id,
                  rating: property.rating,
                  guests: property.guests,
                  reviews: property.reviews,
                  images: property.images,
                  host: property.host,
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <MobileBookingBar
        price={property.price}
        checkIn={checkInDate ?? undefined}
        checkOut={checkOutDate ?? undefined}
        onDateChange={handleDateChange}
        unavailableDates={unavailableDates}
        propertyTitle={property.title}
        property={{
          rating: property.rating,
          guests: property.guests,
          reviews: property.reviews,
          images: property.images,
          host: property.host,
        }}
        maxGuests={property.guests}
      />
    </Layout>
  );
});

PropertyDetail.displayName = 'PropertyDetail';
export default PropertyDetail;
